import {
  OFFER, OPTION, ANNUAL_COSTS, MODULES, CHALLENGES, PHASES, TEAM, CLIENT_TEAM,
  GOVERNANCE, TERMS, ASSUMPTIONS, EXCLUSIONS,
  MILESTONE_PCTS, MILESTONE_NAMES, MILESTONE_DUES, fmt,
} from "./data";

const milestones = MILESTONE_PCTS.map((pct, i) => ({
  name: MILESTONE_NAMES[i],
  pct,
  total: Math.round((OPTION.base * pct) / 100),
  due: MILESTONE_DUES[i],
}));
const annualTotal = ANNUAL_COSTS.reduce((s, c) => s + c.aed, 0) + OPTION.support;

const S = ({ n, title }) => (
  <div className="mt-10 mb-4 border-b-2 border-amber-700 pb-2 break-after-avoid">
    <p className="font-mono2 text-[9px] tracking-[0.3em] text-amber-700 uppercase">Section {n}</p>
    <h2 className="font-display text-xl font-bold text-gray-900">{title}</h2>
  </div>
);

const TH = ({ children }) => (
  <th className="border border-gray-300 bg-gray-100 px-3 py-2 text-left font-mono2 text-[9px] font-semibold tracking-[0.15em] text-gray-600 uppercase">
    {children}
  </th>
);
const TD = ({ children, className = "" }) => (
  <td className={`border border-gray-300 px-3 py-2 text-[11px] text-gray-800 ${className}`}>{children}</td>
);

export const PrintDocument = () => (
  <div data-testid="print-document" className="hidden bg-white font-display text-gray-900 print:block">
    {/* Cover */}
    <div className="border-b-4 border-amber-700 pb-6">
      <p className="font-mono2 text-[10px] tracking-[0.35em] text-amber-700 uppercase">
        Commercial Offer · {OFFER.ref}
      </p>
      <h1 className="mt-3 text-3xl font-extrabold leading-tight">
        Odoo ERP Implementation Services
      </h1>
      <p className="mt-2 text-sm text-gray-600">
        Digital transformation of Vision Oil Field Equipment LLC — four-country operations
        (UAE · KSA · Qatar · Egypt) on one integrated Odoo platform, delivered in 6–7 months.
      </p>
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div className="border border-gray-300 p-4">
          <p className="font-mono2 text-[9px] tracking-[0.25em] text-gray-500 uppercase">Issued By</p>
          <p className="mt-1 text-sm font-bold">{OFFER.consultant}</p>
          <p className="text-xs text-gray-600">{OFFER.consultantRole}</p>
        </div>
        <div className="border border-gray-300 p-4">
          <p className="font-mono2 text-[9px] tracking-[0.25em] text-gray-500 uppercase">Issued To</p>
          <p className="mt-1 text-sm font-bold">{OFFER.client}</p>
          {OFFER.contacts.map((c) => (
            <p key={c.name} className="text-xs text-gray-600">{c.name} · {c.role}</p>
          ))}
        </div>
      </div>
      <div className="mt-4 flex gap-8 text-xs text-gray-600">
        <span><strong className="text-gray-900">Date:</strong> {OFFER.date}</span>
        <span><strong className="text-gray-900">Valid until:</strong> {OFFER.validUntil}</span>
        <span><strong className="text-gray-900">Reference:</strong> {OFFER.ref}</span>
      </div>
    </div>

    {/* Executive summary */}
    <S n="01" title="Executive Summary" />
    <p className="text-[12px] leading-relaxed text-gray-700">
      Vision Oil Field Equipment operates across four countries, eleven engineered equipment lines and
      seven service streams. This engagement consolidates manufacturing, rentals, field service,
      procurement, HR and finance onto one integrated Odoo platform, with e-invoicing compliance for
      the UAE, Saudi Arabia and Egypt built in from day one. The engagement is fixed-scope and phased
      over 6–7 months — operations first, finance last — with a stage-gate at the end of every phase,
      and a live Vision-loaded Odoo environment delivered within the first 2–3 weeks.
    </p>

    {/* Challenges */}
    <S n="02" title="The Ten Challenges Addressed" />
    <table className="w-full border-collapse break-inside-avoid">
      <thead>
        <tr><TH>#</TH><TH>Area</TH><TH>Challenge</TH><TH>Odoo Solution</TH></tr>
      </thead>
      <tbody>
        {CHALLENGES.map((c, i) => (
          <tr key={c.title}>
            <TD className="w-8 font-semibold text-amber-700">{String(i + 1).padStart(2, "0")}</TD>
            <TD className="w-24">{c.area}</TD>
            <TD>{c.title}</TD>
            <TD className="font-medium">{c.solution}</TD>
          </tr>
        ))}
      </tbody>
    </table>

    {/* Scope */}
    <div className="break-before-page" />
    <S n="03" title="Scope of Work — Odoo Application Map" />
    <table className="w-full border-collapse">
      <tbody>
        {MODULES.map((m, i) => (
          <tr key={m.name} className="break-inside-avoid">
            <TD className="w-8 font-semibold text-amber-700">{String(i + 1).padStart(2, "0")}</TD>
            <TD className="w-44 font-semibold">{m.name}</TD>
            <TD>{m.desc}</TD>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-3 text-[11px] text-gray-600">
      Delivered on Odoo Enterprise with certified country localizations (UAE FTA-ready, KSA ZATCA
      Fatoora, Egypt ETA), full Arabic UI & RTL, and Odoo Studio extensions for the rental contract
      model and procurement approval matrix.
    </p>

    {/* Roadmap */}
    <div className="break-before-page" />
    <S n="04" title="Implementation Roadmap — 6 to 7 Months" />
    <table className="w-full border-collapse">
      <thead>
        <tr><TH>Phase</TH><TH>Title</TH><TH>Duration</TH><TH>Scope & Deliverable</TH></tr>
      </thead>
      <tbody>
        {PHASES.map((p) => (
          <tr key={p.phase} className="break-inside-avoid">
            <TD className="w-16 font-semibold text-amber-700">{p.phase}</TD>
            <TD className="w-36 font-semibold">{p.title}</TD>
            <TD className="w-20">{p.duration}</TD>
            <TD>{p.deliverable}</TD>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-3 text-[11px] text-gray-600">
      Operations are built first; finance, invoicing and statutory compliance land in Phase 04, with
      Go-Live aligned to a fiscal period boundary. Each phase ends with a stable, usable system.
    </p>

    {/* Team */}
    <S n="05" title="Delivery Team & Governance" />
    <table className="w-full border-collapse break-inside-avoid">
      <tbody>
        {TEAM.map((t) => (
          <tr key={t.role}>
            <TD className="w-56 font-semibold">{t.role}</TD>
            <TD>{t.scope}</TD>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-3 text-[11px] text-gray-700">
      <strong>Client-side:</strong> {CLIENT_TEAM.join(" · ")}
    </p>
    <p className="mt-1 text-[11px] text-gray-700">
      <strong>Governance:</strong> {GOVERNANCE.join(" · ")}
    </p>

    {/* Investment */}
    <div className="break-before-page" />
    <S n="06" title="Investment" />
    <div className="border-2 border-amber-700 p-5 break-inside-avoid">
      <p className="font-mono2 text-[9px] tracking-[0.25em] text-amber-700 uppercase">{OPTION.name}</p>
      <p className="text-[10px] text-gray-500">{OPTION.tag}</p>
      <p className="mt-3 text-[13px] text-gray-500">
        Standard rate: <span className="line-through">AED {fmt(OPTION.originalBase)}</span>
      </p>
      <p className="mt-1 text-3xl font-extrabold text-amber-700">
        AED {fmt(OPTION.total)}{" "}
        <span className="text-xs font-semibold text-gray-600">
          — special rate, save AED {fmt(OPTION.originalBase - OPTION.total)}
        </span>
      </p>
      <p className="mt-1 text-[10px] text-gray-600">{OPTION.vatNote}</p>
      <div className="mt-4 border-2 border-red-700 bg-red-50 p-3 break-inside-avoid">
        <p className="text-[11px] font-extrabold uppercase tracking-wide text-red-700">
          Important — not included in the implementation fee
        </p>
        <p className="mt-1 text-[11px] leading-relaxed text-gray-800">
          The AED {fmt(OPTION.total)} covers implementation services only. The Odoo Enterprise license
          (AED {fmt(ANNUAL_COSTS[1].aed)}/year) and odoo.sh hosting (AED {fmt(ANNUAL_COSTS[0].aed)}/year)
          are <strong>not included — not in the first year, not in any year</strong> — and are paid
          directly to Odoo from the first year onwards.
        </p>
      </div>
      <table className="mt-4 w-full border-collapse">
        <thead>
          <tr><TH>Milestone</TH><TH>%</TH><TH>Amount</TH><TH>Due</TH></tr>
        </thead>
        <tbody>
          {milestones.map((m) => (
            <tr key={m.name}>
              <TD>{m.name}</TD>
              <TD className="w-10 text-center">{m.pct}%</TD>
              <TD className="w-24 font-semibold">AED {fmt(m.total)}</TD>
              <TD>{m.due}</TD>
            </tr>
          ))}
          <tr className="bg-amber-50">
            <TD className="font-bold">Total Contract Value</TD>
            <TD className="text-center font-bold">100%</TD>
            <TD className="font-extrabold text-amber-700">AED {fmt(OPTION.total)}</TD>
            <TD>No VAT</TD>
          </tr>
        </tbody>
      </table>
    </div>

    <p className="mt-6 mb-1 text-sm font-bold break-after-avoid">Annual Recurring Costs</p>
    <p className="mb-2 text-[10px] text-gray-600 break-after-avoid">
      Odoo Enterprise license and odoo.sh hosting are not included in the implementation fee — they are
      paid directly to Odoo; support is billed by the consultant.
    </p>
    <table className="w-full border-collapse break-inside-avoid">
      <thead>
        <tr><TH>Item</TH><TH>Amount / Year</TH><TH>Notes</TH></tr>
      </thead>
      <tbody>
        {ANNUAL_COSTS.map((c) => (
          <tr key={c.name}>
            <TD className="w-44 font-semibold">{c.name}</TD>
            <TD className="w-40 font-semibold">AED {fmt(c.aed)} (≈ USD {c.usd})</TD>
            <TD>{c.note}</TD>
          </tr>
        ))}
        <tr>
          <TD className="font-semibold">Annual Support</TD>
          <TD className="font-semibold">AED {fmt(OPTION.support)}</TD>
          <TD>20% of the contract value, billed from Go-Live.</TD>
        </tr>
        <tr className="bg-amber-50">
          <TD className="font-bold">Total Annual</TD>
          <TD className="font-extrabold text-amber-700">AED {fmt(annualTotal)}</TD>
          <TD />
        </tr>
      </tbody>
    </table>

    {/* Terms */}
    <div className="break-before-page" />
    <S n="07" title="Terms, Assumptions & Exclusions" />
    {TERMS.map((t, i) => (
      <p key={t.title} className="mt-2 text-[11px] leading-relaxed text-gray-700 break-inside-avoid">
        <strong>§{i + 1} {t.title}.</strong> {t.desc}
      </p>
    ))}
    <div className="mt-4 grid grid-cols-2 gap-4 break-inside-avoid">
      <div className="border border-gray-300 p-4">
        <p className="font-mono2 text-[9px] tracking-[0.25em] text-amber-700 uppercase">Assumptions</p>
        {ASSUMPTIONS.map((a) => (
          <p key={a.id} className="mt-2 text-[11px] text-gray-700"><strong>{a.id}.</strong> {a.text}</p>
        ))}
      </div>
      <div className="border border-gray-300 p-4">
        <p className="font-mono2 text-[9px] tracking-[0.25em] text-gray-500 uppercase">Exclusions</p>
        {EXCLUSIONS.map((e) => (
          <p key={e.id} className="mt-2 text-[11px] text-gray-700"><strong>{e.id}.</strong> {e.text}</p>
        ))}
      </div>
    </div>

    {/* Acceptance */}
    <S n="08" title="Acceptance & Authorization" />
    <p className="border border-gray-300 px-3 py-2 text-[12px] font-semibold">
      ☐&nbsp;&nbsp;Accepted — {OPTION.name} — AED {fmt(OPTION.total)} (no VAT) — special rate, reduced
      from AED {fmt(OPTION.originalBase)}
    </p>
    <div className="mt-8 grid grid-cols-2 gap-8 break-inside-avoid">
      {[
        { who: `For ${OFFER.client}`, sub: "Authorized signatory" },
        { who: `For ${OFFER.consultant}`, sub: OFFER.consultantRole },
      ].map((b) => (
        <div key={b.who}>
          <p className="text-[11px] font-bold">{b.who}</p>
          <p className="text-[10px] text-gray-500">{b.sub}</p>
          {["Name & Title", "Signature", "Date"].map((l) => (
            <div key={l} className="mt-6 border-b border-gray-400 pb-1">
              <span className="font-mono2 text-[9px] tracking-[0.2em] text-gray-500 uppercase">{l}</span>
            </div>
          ))}
        </div>
      ))}
    </div>

    <p className="mt-10 border-t border-gray-300 pt-3 text-center font-mono2 text-[9px] tracking-[0.3em] text-gray-500 uppercase">
      {OFFER.consultant} × {OFFER.client} · {OFFER.ref} · {OFFER.date}
    </p>
  </div>
);
